import { Section, Reveal, Overline } from "./primitives";
import { SERVICES } from "../../data";
import { useSiteContent } from "../../lib/content";
import { motion } from "framer-motion";
import { ArrowUpRight, Award, Gauge, LineChart, Eye } from "lucide-react";

const EXCELLENCE = [
  { icon: Award, title: "Awarded Craft", body: "Design and film that earns attention on the world stage." },
  { icon: LineChart, title: "Measured Growth", body: "Every rupee optimised for revenue, not vanity metrics." },
  { icon: Gauge, title: "Rapid Delivery", body: "Senior pods ship in weeks — never at the cost of craft." },
  { icon: Eye, title: "Radical Transparency", body: "Real numbers and honest updates every single week." },
];

export default function Services() {
  const { content } = useSiteContent();
  // Merge text from content by index; icons/img/span always from defaults (Phase 3 will edit images).
  const items = SERVICES.map((base, i) => {
    const o = content.services?.[i] || {};
    return {
      ...base,
      title: o.title || base.title,
      tag: o.tag || base.tag,
      items: (Array.isArray(o.items) && o.items.length ? o.items : base.items),
    };
  });
  return (
    <Section id="services" className="noise-bg">
      <Reveal>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <Overline>Our Services</Overline>
            <h2 className="mt-6 font-display font-bold text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              Ten disciplines.<br /><span className="text-gradient">One growth engine.</span>
            </h2>
          </div>
          <p className="max-w-sm text-muted-foreground">
            From the keynote stage to the landing page — every service is built to move
            your business forward.
          </p>
        </div>
      </Reveal>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 auto-rows-[minmax(240px,auto)]">
        {SERVICES.map((s, i) => (
          <Reveal key={s.title} delay={(i % 3) * 0.08} className={s.span}>
            <motion.div
              whileHover={{ y: -6 }}
              transition={{ type: "spring", stiffness: 300, damping: 24 }}
              className="group relative h-full rounded-3xl border border-border/10 bg-card overflow-hidden shimmer-card"
            >
              {s.img && (
                <div className="absolute inset-0">
                  <img src={s.img} alt={s.title} className="h-full w-full object-cover opacity-30 group-hover:opacity-45 group-hover:scale-105 transition-all duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-card via-card/85 to-card/40" />
                </div>
              )}
              <div className="relative z-10 p-8 flex flex-col h-full">
                <div className="flex items-center justify-between">
                  <div className="h-12 w-12 grid place-items-center rounded-2xl bg-brand-gradient text-white">
                    <s.icon className="h-6 w-6" />
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary">{s.tag}</span>
                </div>
                <h3 className="mt-6 font-display text-2xl font-bold tracking-tight">{s.title}</h3>
                <div className="mt-4 flex flex-wrap gap-2">
                  {s.items.map((it) => (
                    <span key={it} className="rounded-full border border-border/15 bg-background/40 px-3 py-1 text-xs text-muted-foreground">{it}</span>
                  ))}
                </div>
                <div className="mt-auto pt-6 flex items-center gap-2 text-sm font-semibold text-primary opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all">
                  Explore service <ArrowUpRight className="h-4 w-4" />
                </div>
              </div>
            </motion.div>
          </Reveal>
        ))}
      </div>

      {/* Service Excellence pillars */}
      <Reveal>
        <div className="mt-16 rounded-[2rem] border border-border/10 bg-card/60 backdrop-blur p-8 md:p-10 shimmer-card">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
            <div>
              <Overline>Service Excellence</Overline>
              <h3 className="mt-4 font-display text-2xl md:text-3xl font-bold tracking-tight">The standards behind every project.</h3>
            </div>
            <p className="max-w-sm text-sm text-muted-foreground">Non-negotiables our clients feel from kickoff to reporting.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {EXCELLENCE.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -4 }}
                className="group rounded-2xl border border-border/10 bg-background/40 p-6"
              >
                <div className="h-11 w-11 grid place-items-center rounded-xl border border-border/15 text-primary group-hover:bg-brand-gradient group-hover:text-white group-hover:border-transparent transition-colors">
                  <p.icon className="h-5 w-5" />
                </div>
                <div className="mt-5 font-display font-bold">{p.title}</div>
                <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{p.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </Reveal>

    </Section>
  );
}
